import { render } from 'react-dom';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import flagsmith from '@flagsmith/flagsmith';
import { FlagsmithProvider } from '@flagsmith/flagsmith/react';
// Legacy Contexts
import { CurrentUserProvider } from 'contexts/CurrentUser';
import { AddressesProvider } from 'contexts/Addresses';
import { SubscriptionProvider } from 'contexts/Subscription';
import { UpcomingMenusProvider } from 'contexts/UpcomingMenus';
import { NotificationsProvider } from 'contexts/Notifications';
import { FavoritesProvider } from 'contexts/Favorites';
// Legacy Components
import SubscriptionPage from 'components/MyAccount/SubscriptionPage';
import AccountSettings from 'components/MyAccount/AccountSettings';
import PaymentMethod from 'components/MyAccount/PaymentMethod';
import DeliveryAddress from 'components/MyAccount/DeliveryAddress';
import NotificationPreferencePage from 'components/MyAccount/NotificationPreferencePage';
import ManageSubscription from 'components/ManageSubscription';
import OrderHistory from 'components/OrderHistory';
import TrackingPage from 'components/TrackingPage';
import Favorites from 'components/Favorites';
import RedeemGift from 'components/RedeemGift';
import Giveaway from 'components/Giveaway';
import Refer from 'components/Refer';
import SignUp from 'components/App/layouts/SignUp';
import Plan from 'components/SignUp/Plan';
import Checkout from 'components/SignUp/Checkout';
// Platform Contexts
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { MenuWithCartProvider } from './contexts/MenuWithCart';
import { NotificationProvider } from './contexts/NotificationContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { AddressProvider } from './contexts/AddressContext';
// Platform Components
import Layout from './layouts/Layout';
import { Route as ConfigRoute } from './utils/config';
import ProductDisplayPage from './components/product-display-page/ProductDisplayPage';
import MenuPageContainer from './components/menu-page/MenuPageContainer';
import { SignupStep } from './constants/signup';
import RobinArzonLimitedTimeOfferingPDP
  from './components/limited-time-offering/RobinArzonLimitedTimeOfferingPDP';
import { OrderConfirmationPage } from './components/order-confirmation/OrderConfirmationPage';

document.addEventListener('DOMContentLoaded', () => {
  const reactPlatformElement = document.getElementById('platform');
  if (!reactPlatformElement) return;

  const isPlatformHubProspectEnabled = window.gon?.featureFlags?.isPlatformHubProspectEnabled;
  const isLimitedTimeOfferingEnabled = window.gon?.featureFlags?.isLimitedTimeOfferingEnabled;
  const currentSignupStep = reactPlatformElement?.getAttribute('data-signup-step') as SignupStep;
  const isProspect = document.cookie.includes('is_prospect_user=true');
  // user is considered "logged in" if they're not a prospect AND they've completed all signup steps
  const isLoggedIn = !isProspect && !currentSignupStep;
  const hasAccount = !!currentSignupStep || isLoggedIn;
  const queryClient = new QueryClient();

  render(
    <FlagsmithProvider
      options={{
        environmentID: FLAGSMITH_ENVIRONMENT_KEY,
        cacheFlags: true,
        cacheOptions: {
          ttl: 300000, // 5 mins
        },
      }}
      flagsmith={flagsmith}
    >
      <MantineProvider>
        <BrowserRouter>
          <AuthProvider isLoggedIn={isLoggedIn}>
            <QueryClientProvider client={queryClient}>
              <CurrentUserProvider>
                <UserProvider hasAccount={hasAccount}>
                  <AddressProvider>
                    <PaymentProvider>
                      <AddressesProvider>
                        <MenuWithCartProvider signupStep={currentSignupStep}>
                          <SubscriptionProvider>
                            <UpcomingMenusProvider>
                              <NotificationProvider>
                                <Routes>
                                  {isPlatformHubProspectEnabled && (
                                    <Route
                                      path="/"
                                      element={<Layout withCart />}
                                    >
                                      <Route
                                        path={ConfigRoute.SHOP}
                                        element={<MenuPageContainer />}
                                      />
                                    </Route>
                                  )}
                                  {isLimitedTimeOfferingEnabled && (
                                    <Route
                                      path="/"
                                      element={<Layout withCart isPDP />}
                                    >
                                      <Route
                                        path={ConfigRoute.ROBIN_ARZON}
                                        element={<RobinArzonLimitedTimeOfferingPDP />}
                                      />
                                    </Route>
                                  )}
                                  {/* Order Confirmation */}
                                  <Route
                                    path="/"
                                    element={<Layout withCart isWide />}
                                  >
                                    <Route
                                      path={ConfigRoute.ORDER_CONFIRMATION}
                                      element={<OrderConfirmationPage />}
                                    />
                                  </Route>

                                  {/* Cart-Enabled Routes */}
                                  <Route
                                    path="/"
                                    element={<Layout withCart />}
                                  >
                                    <Route
                                      path={ConfigRoute.HUB}
                                      element={<MenuPageContainer />}
                                    />
                                  </Route>
                                  <Route
                                    path="/"
                                    element={<Layout withCart isPDP />}
                                  >
                                    <Route
                                      path={ConfigRoute.PDP}
                                      element={<ProductDisplayPage />}
                                    />
                                  </Route>

                                  {/* Account Management Routes */}
                                  <Route
                                    path="/"
                                    element={<Layout withAccountLayout />}
                                  >
                                    <Route
                                      path={ConfigRoute.PROFILE}
                                      element={(
                                        <NotificationsProvider>
                                          <SubscriptionPage />
                                        </NotificationsProvider>
                                      )}
                                    />
                                    <Route
                                      path={ConfigRoute.ACCOUNT_SETTINGS}
                                      element={<AccountSettings />}
                                    />
                                    <Route
                                      path={ConfigRoute.PAYMENT_METHOD}
                                      element={<PaymentMethod />}
                                    />
                                    <Route
                                      path={ConfigRoute.DELIVERY_ADDRESS}
                                      element={(
                                        <NotificationsProvider>
                                          <DeliveryAddress />
                                        </NotificationsProvider>
                                      )}
                                    />
                                    <Route
                                      path={ConfigRoute.NOTIFICATION_PREFERENCES}
                                      element={<NotificationPreferencePage />}
                                    />
                                  </Route>

                                  {/* Misc. Legacy Routes */}
                                  <Route
                                    path="/"
                                    element={<Layout />}
                                  >
                                    <Route
                                      path={ConfigRoute.ORDER_HISTORY}
                                      element={<OrderHistory />}
                                    />
                                    <Route
                                      path={ConfigRoute.TRACKING}
                                      element={<TrackingPage />}
                                    />
                                    <Route
                                      path={ConfigRoute.MANAGE_SUBSCRIPTION}
                                      element={<ManageSubscription />}
                                    />
                                    <Route
                                      path={ConfigRoute.MY_FAVORITES}
                                      element={(
                                        <FavoritesProvider>
                                          <Favorites />
                                        </FavoritesProvider>
                                      )}
                                    />
                                    <Route
                                      path={ConfigRoute.REDEEM_CODE}
                                      element={<RedeemGift />}
                                    />
                                    <Route
                                      path={ConfigRoute.REFER}
                                      element={<Refer />}
                                    />
                                    <Route
                                      path={ConfigRoute.SHARE_A_BOX}
                                      element={<Giveaway />}
                                    />
                                  </Route>

                                  {/* Legacy Signup Routes */}
                                  <Route
                                    path={ConfigRoute.SIGNUP_PLAN}
                                    element={<SignUp />}
                                  >
                                    <Route
                                      path="plan"
                                      element={<Plan />}
                                    />
                                  </Route>

                                  <Route
                                    path={ConfigRoute.SIGNUP_CHECKOUT}
                                    element={<SignUp />}
                                  >
                                    <Route
                                      path=":step"
                                      element={<Checkout />}
                                    />
                                  </Route>

                                  <Route
                                    path={ConfigRoute.SIGNUP_CHECKOUT_REDEEM}
                                    element={<SignUp />}
                                  >
                                    <Route
                                      path="gift/:step"
                                      element={<Checkout />}
                                    />
                                  </Route>

                                  <Route
                                    path={ConfigRoute.SIGNUP_CHECKOUT_REDEEM}
                                    element={<SignUp />}
                                  >
                                    <Route
                                      path="giveaway/:step"
                                      element={<Checkout />}
                                    />
                                  </Route>
                                </Routes>
                              </NotificationProvider>
                            </UpcomingMenusProvider>
                          </SubscriptionProvider>
                        </MenuWithCartProvider>
                      </AddressesProvider>
                    </PaymentProvider>
                  </AddressProvider>
                </UserProvider>
              </CurrentUserProvider>
            </QueryClientProvider>
          </AuthProvider>
        </BrowserRouter>
      </MantineProvider>
    </FlagsmithProvider>,
    reactPlatformElement,
  );
});
